import React from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Chip,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { DataGrid, GridActionsCellItem, GridToolbar } from "@mui/x-data-grid";
import {
  Backspace,
  BadgeTwoTone,
  DeleteTwoTone,
  Security,
} from "@mui/icons-material";
import SendEmail from "../Components/SendEmail";
import CertificateIcon from "@mui/icons-material/WorkspacePremium";
import DeleteConfirmationDialog, {
  useDeleteConfirmation,
} from "../Components/DeleteConfirmationDialog";

// All possible permission actions for each role type
const permissionActions = [
  "imp",
  "reg",
  "exp",
  "del",
  "edit",
  "badge",
  "find",
  "graph",
  "cert",
];

// Non-role-related permissions that should always be included
const globalPermissions = [
  "admin",
  "superadmin",
  "bulk-email",
  "leadgen",
  "emailtracking",
  "showsponsors",
  "role-switcher",
  "importhistory",
  "tokenLink",
  "upgrade-registration",
  "showcorporates",
  "showexhibitors",
  "generatePromo",
  "all",
  "closed",
  "graph-dash",
];

// Generate dynamic permission options based on allowed roles
const getPermissionOptions = (allowedRoles = []) => {
  const roleBasedPermissions = [];

  // Generate permissions for each allowed role
  allowedRoles.forEach((role) => {
    permissionActions.forEach((action) => {
      roleBasedPermissions.push(`${action}-${role}`);
    });
  });

  // Combine role-based permissions with global permissions
  return [...roleBasedPermissions, ...globalPermissions].sort();
};

export default function RoleDataPage() {
  const { eventId, role } = useParams();
  const [search, setSearch] = React.useState("");

  return (
    <Stack spacing={2}>
      <Typography variant="h4" sx={{ textTransform: "capitalize" }}>
        {role} Data
      </Typography>

      <SearchBox onSearch={setSearch} />
      <DataTable eventId={eventId} role={role} search={search} />
    </Stack>
  );
}

function DataTable({ eventId, role, search }) {
  const limit = 100;
  const { deleteState, openDeleteDialog, closeDeleteDialog, setLoading } =
    useDeleteConfirmation();

  const { data, loading, error, refetch } = useQuery(GET_DATA, {
    variables: { eventId, role, conditions: { limit, text: search } },
    onError: (e) => console.log(JSON.stringify(e, null, 2)),
  });

  // Fetch allowed roles for permission editing
  const { data: allowedRolesData } = useQuery(GET_ALLOWED_ROLES, {
    variables: { eventId },
    skip: !eventId,
  });

  const [doDelete] = useMutation(DELETE_APPLICANT, { onCompleted: refetch });
  const [updateField] = useMutation(UPDATE_APPLICANT_FIELD, {
    onCompleted: refetch,
  });

  const handleDeleteConfirmed = async (deleteReason) => {
    setLoading(true);
    try {
      await doDelete({
        variables: {
          eventId,
          role,
          id: deleteState.item.id,
          deleteReason,
        },
      });
      closeDeleteDialog();
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete applicant: " + error.message);
    } finally {
      setLoading(false);
    }
  };
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [permissionEditOpen, setPermissionEditOpen] = React.useState(false);

  // Check if form has permission fields
  const hasPermissionFields = data?.form.fields.some(
    (f) => f.type === "permission"
  );
  const permissionFields =
    data?.form.fields.filter((f) => f.type === "permission") || [];

  if (loading) {
    return <LinearProgress />;
  }
  if (error) {
    return <Typography color="error">{error.message}</Typography>;
  }

  return (
    <Stack spacing={2}>
      <DeleteConfirmationDialog
        open={deleteState.open}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteConfirmed}
        title="Delete Applicant"
        itemName={deleteState.item?.email || deleteState.item?.id}
        itemType="applicant"
        loading={deleteState.loading}
      />
      <Box>
        <DataGrid
          paginationMode="server"
          pageSize={limit}
          rowsPerPageOptions={[limit]}
          onPageChange={(p) =>
            refetch({
              conditions: {
                offset: p * limit,
                limit,
              },
            })
          }
          rowCount={data?.total || 0}
          sx={{ height: 600 }}
          // experimentalFeatures={{ newEditingApi: true }}
          checkboxSelection
          onSelectionModelChange={(ids) => {
            setSelectedIds(
              ids.map((id) => {
                const applicant = JSON.parse(
                  data?.applicants?.find((d) => d.id === id)?.data ||
                    "{email:'undefined'}"
                );
                return {
                  id,
                  email: applicant.email || applicant.Email,
                  data: applicant,
                };
              })
            );
          }}
          onCellEditCommit={(p, e) => {
            // Skip permission fields - they should use the special editor
            if (permissionFields.some((f) => f.name === p.field)) {
              return;
            }

            if (e.key === "Enter") {
              updateField({
                variables: {
                  eventId,
                  role,
                  id: p.id,
                  field: p.field,
                  value: p.value,
                },
              }).catch((e) => window.alert(e.message));
            }
          }}
          components={{
            Toolbar: GridToolbar,
          }}
          columns={[
            {
              type: "actions",
              field: "actions",
              headerName: "Actions",
              width: 150,
              getActions: (p) => [
                <GridActionsCellItem
                  label="Delete"
                  onClick={() => {
                    const applicantData = JSON.parse(p.data || "{}");
                    openDeleteDialog(
                      {
                        id: p.id,
                        email: applicantData.email || applicantData.Email,
                        data: applicantData,
                      },
                      "applicant"
                    );
                  }}
                  icon={<DeleteTwoTone color="error" />}
                />,
                <Divider flexItem orientation="vertical" />,
                <GridActionsCellItem
                  label="Badge"
                  onClick={() =>
                    window.open(
                      `https://api-reg.eventegration.com/graphql/badge/${eventId}/${role}/${p.id}`
                    )
                  }
                  icon={<BadgeTwoTone color="success" />}
                />,
                <Divider flexItem orientation="vertical" />,
                <GridActionsCellItem
                  label="Certificate"
                  onClick={() =>
                    window.open(
                      `https://api-reg.eventegration.com/graphql/certificate/${eventId}/${role}/${p.id}`
                    )
                  }
                  icon={<CertificateIcon color="primary" />}
                />,
              ],
            },
            { field: "id", width: 110 },

            {
              field: "created",
              width: 200,
              valueFormatter: (row) =>
                format(+row.value, "dd-MMM-yyyy hh:mm a"),
            },
            { field: "regLink", width: 300, headerName: "Registration Link" },

            ...data?.form.fields.map((f) => ({
              field: f.name,
              width:
                f.name === "permissions" || f.type === "permission" ? 600 : 200,
              editable: f.type !== "permission", // Disable direct editing for permission fields
              renderCell:
                f.type === "permission"
                  ? (params) => {
                      const permissions = params.value
                        ? params.value.split(",")
                        : [];
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            maxWidth: 580,
                          }}
                        >
                          {permissions.slice(0, 3).map((permission, index) => (
                            <Chip
                              key={index}
                              label={permission.trim()}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          ))}
                          {permissions.length > 3 && (
                            <Chip
                              label={`+${permissions.length - 3} more`}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          )}
                        </Box>
                      );
                    }
                  : undefined,
            })),
          ]}
          rows={data?.applicants.map((row) => ({
            ...row,
            ...JSON.parse(row.data),
          }))}
        />
      </Box>
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <Button
          variant="contained"
          disabled={loading}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
        <SendEmail {...{ selectedIds, eventId, role }} />

        {/* Edit Permission Button */}
        {hasPermissionFields && selectedIds.length > 0 && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Security />}
            onClick={() => setPermissionEditOpen(true)}
          >
            Edit Permissions ({selectedIds.length})
          </Button>
        )}
      </Stack>

      {/* Permission Edit Modal */}
      <PermissionEditModal
        open={permissionEditOpen}
        onClose={() => setPermissionEditOpen(false)}
        selectedIds={selectedIds}
        permissionFields={permissionFields}
        allowedRoles={allowedRolesData?.allowedRoles || []}
        eventId={eventId}
        role={role}
        onUpdate={refetch}
      />

      <Box p={4} />
    </Stack>
  );
}

function PermissionEditModal({
  open,
  onClose,
  selectedIds,
  permissionFields,
  allowedRoles,
  eventId,
  role,
  onUpdate,
}) {
  const [selectedPermissions, setSelectedPermissions] = React.useState({});
  const [customPermissions, setCustomPermissions] = React.useState({});
  const [updateField] = useMutation(UPDATE_APPLICANT_FIELD);

  const availablePermissions = getPermissionOptions(allowedRoles);

  // Initialize permissions state when modal opens
  React.useEffect(() => {
    if (open && selectedIds.length > 0) {
      const initialSelected = {};
      const initialCustom = {};

      permissionFields.forEach((field) => {
        initialSelected[field.name] = [];
        initialCustom[field.name] = "";

        selectedIds.forEach((selected) => {
          const currentValue = selected.data[field.name] || "";
          const currentPermissions = currentValue
            .split(",")
            .filter((p) => p.trim());

          // For bulk editing, start with common permissions
          if (initialSelected[field.name].length === 0) {
            initialSelected[field.name] = currentPermissions;
          }
        });
      });

      setSelectedPermissions(initialSelected);
      setCustomPermissions(initialCustom);
    }
  }, [open, selectedIds, permissionFields]);

  const handlePermissionToggle = (fieldName, permission) => {
    setSelectedPermissions((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].includes(permission)
        ? prev[fieldName].filter((p) => p !== permission)
        : [...prev[fieldName], permission],
    }));
  };

  const handleSelectAll = (fieldName) => {
    setSelectedPermissions((prev) => ({
      ...prev,
      [fieldName]: availablePermissions,
    }));
  };

  const handleSelectNone = (fieldName) => {
    setSelectedPermissions((prev) => ({
      ...prev,
      [fieldName]: [],
    }));
  };

  const handleAddCustomPermissions = (fieldName) => {
    if (customPermissions[fieldName]?.trim()) {
      const customPerms = customPermissions[fieldName]
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p && !selectedPermissions[fieldName].includes(p));

      setSelectedPermissions((prev) => ({
        ...prev,
        [fieldName]: [...prev[fieldName], ...customPerms],
      }));

      setCustomPermissions((prev) => ({
        ...prev,
        [fieldName]: "",
      }));
    }
  };

  const handleSave = async () => {
    const updates = [];

    // Prepare updates for each selected applicant and each permission field
    selectedIds.forEach((selected) => {
      permissionFields.forEach((field) => {
        const newValue = selectedPermissions[field.name]?.join(",") || "";
        updates.push({
          id: selected.id,
          field: field.name,
          value: newValue,
        });
      });
    });

    // Execute all updates
    try {
      await Promise.all(
        updates.map((update) =>
          updateField({
            variables: {
              eventId,
              role,
              id: update.id,
              field: update.field,
              value: update.value,
            },
          })
        )
      );

      onUpdate();
      onClose();
    } catch (error) {
      console.error("Error updating permissions:", error);
      window.alert("Error updating permissions: " + error.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Security color="primary" />
          Edit Permissions for {selectedIds.length} Selected{" "}
          {selectedIds.length === 1 ? "Record" : "Records"}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          {permissionFields.map((field) => (
            <Box key={field.name} mb={4}>
              <Typography variant="h6" gutterBottom>
                {field.label || field.name}
              </Typography>

              {/* Action Buttons */}
              <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                <Button
                  onClick={() => handleSelectAll(field.name)}
                  variant="outlined"
                  size="small"
                  startIcon={<span>✓</span>}
                >
                  Select All ({availablePermissions.length})
                </Button>
                <Button
                  onClick={() => handleSelectNone(field.name)}
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<span>✗</span>}
                >
                  Clear All
                </Button>
                <Typography variant="body2" sx={{ alignSelf: "center", ml: 2 }}>
                  Selected: {selectedPermissions[field.name]?.length || 0}
                </Typography>
              </Box>

              {/* Permission Checkboxes */}
              <FormGroup>
                <Grid container spacing={1}>
                  {availablePermissions.map((permission, i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              selectedPermissions[field.name]?.includes(
                                permission
                              ) || false
                            }
                            onChange={() =>
                              handlePermissionToggle(field.name, permission)
                            }
                            size="small"
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.8rem" }}
                          >
                            {permission}
                          </Typography>
                        }
                        sx={{
                          m: 0,
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.8rem",
                          },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>

              {/* Custom Permissions Input */}
              <Box mt={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Add Custom Permissions (comma-separated):
                </Typography>
                <Box display="flex" gap={1}>
                  <TextField
                    value={customPermissions[field.name] || ""}
                    onChange={(e) =>
                      setCustomPermissions((prev) => ({
                        ...prev,
                        [field.name]: e.target.value,
                      }))
                    }
                    placeholder="custom-permission-1, custom-permission-2"
                    size="small"
                    fullWidth
                    variant="outlined"
                  />
                  <Button
                    onClick={() => handleAddCustomPermissions(field.name)}
                    variant="contained"
                    size="small"
                    disabled={!customPermissions[field.name]?.trim()}
                  >
                    Add
                  </Button>
                </Box>
              </Box>

              {/* Selected Permissions Summary */}
              {selectedPermissions[field.name]?.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Permissions:
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 150,
                      overflowY: "auto",
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      p: 1,
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selectedPermissions[field.name].map((perm, index) => (
                        <Chip
                          key={index}
                          label={perm}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Update Permissions
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SearchBox({ onSearch }) {
  const [search, setSearch] = React.useState("");
  React.useEffect(() => {
    const t = setTimeout(() => onSearch(search), 500);
    return () => clearTimeout(t);
  }, [search]);
  return (
    <TextField
      sx={{ maxWidth: 500 }}
      label="Search here..."
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      InputProps={{
        autoComplete: "new-password",
        endAdornment: (
          <InputAdornment position="end">
            {search && (
              <IconButton onClick={() => setSearch("")}>
                <Backspace />
              </IconButton>
            )}
          </InputAdornment>
        ),
      }}
    />
  );
}

const GET_DATA = gql`
  query ($eventId: String!, $role: String!, $conditions: InputConditions) {
    applicants(eventId: $eventId, role: $role, conditions: $conditions) {
      serial
      id
      created
      regLink
      data
    }
    total: applicantsCount(
      eventId: $eventId
      role: $role
      conditions: $conditions
    )
    form(eventId: $eventId, role: $role) {
      fields {
        name
        type
        label
      }
    }
  }
`;

const GET_ALLOWED_ROLES = gql`
  query ($eventId: String!) {
    allowedRoles(eventId: $eventId)
  }
`;

const DELETE_APPLICANT = gql`
  mutation ($eventId: String!, $role: String!, $id: String!) {
    result: deleteApplicant(eventId: $eventId, role: $role, id: $id)
  }
`;

const UPDATE_APPLICANT_FIELD = gql`
  mutation (
    $eventId: String!
    $role: String!
    $id: String!
    $field: String!
    $value: String!
  ) {
    result: updateApplicantField(
      eventId: $eventId
      role: $role
      id: $id
      field: $field
      value: $value
    )
  }
`;
